import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { MessageV2DTO } from 'support/types';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

import { AgentsTabContent } from './AgentsTabContent';
import { AgentErrorBoundary } from './components/AgentErrorBoundary/AgentErrorBoundary';

type AgentsTabProps = {
  message: MessageV2DTO | undefined;
  hasIdentifierErrors: boolean;
  hasEnrichmentApplied: boolean;
  isValidationComplete: boolean;
};

const LoadingContent = (
  <div className="flex justify-center p-8">
    <LoadingSpinner />
  </div>
);

const AgentsTab = ({ message, hasIdentifierErrors, hasEnrichmentApplied, isValidationComplete }: AgentsTabProps) => {
  const { t } = useTranslation();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { data: currentPartner, isLoading: isLoadingPartner } = useCurrentPartner();

  const isLoading = isLoadingUser || isLoadingPartner || !message;

  if (isLoading || !currentUser || !currentPartner) {
    return LoadingContent;
  }

  if (!hasIdentifierErrors && !hasEnrichmentApplied) {
    if (!isValidationComplete) {
      return LoadingContent;
    }

    return (
      <div className="flex flex-col gap-4 p-3">
        <p className="text-sm leading-5 font-normal text-gray-900">
          {t('webedi:sidebar.agents.unsupportedErrorsIntro')}
        </p>
      </div>
    );
  }

  return (
    <AgentErrorBoundary>
      <Suspense fallback={LoadingContent}>
        <AgentsTabContent message={message} currentUser={currentUser} currentPartner={currentPartner} />
      </Suspense>
    </AgentErrorBoundary>
  );
};

export default AgentsTab;
